/**
 * Canvas-Container mit Holopad-Swipe: Schiff folgt dem Finger, gleitet beim Loslassen.
 */
import { SHIP_TYPE_DEFS } from '../config/ShipTypes';

const N = SHIP_TYPE_DEFS.length;
const SWIPE_THRESHOLD = 50;
const SLIDE_DURATION_MS = 200;

function wrapIdx(i: number): number {
  return ((i % N) + N) % N;
}

export interface FleetShipSwipeAreaResult {
  wrap: HTMLDivElement;
  canvas: HTMLCanvasElement;
}

export function createFleetShipSwipeArea(
  getSelectedType: () => number,
  onSelect: (type: number) => void | Promise<void>,
): FleetShipSwipeAreaResult {
  const canvasWrap = document.createElement('div');
  canvasWrap.style.cssText =
    'position:relative;overflow:hidden;touch-action:pan-y;width:100%;height:100%;';
  const inner = document.createElement('div');
  inner.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;';
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
  inner.appendChild(canvas);
  canvasWrap.appendChild(inner);

  let startX = 0;
  let dragging = false;
  let slideBusy = false;

  const clearTransform = () => {
    inner.style.transition = '';
    inner.style.transform = '';
  };

  const applyTx = (px: number) => {
    inner.style.transition = 'none';
    inner.style.transform = `translateX(${px}px)`;
  };
  const slideTo = (dir: 'next' | 'prev') => {
    if (slideBusy) return;
    slideBusy = true;
    const w = canvasWrap.clientWidth;
    if (dir === 'next') {
      inner.style.transition = `transform ${SLIDE_DURATION_MS}ms ease-out`;
      inner.style.transform = `translateX(${-w}px)`;
      inner.ontransitionend = async () => {
        inner.ontransitionend = null;
        const nextType = wrapIdx(getSelectedType() + 1);
        await onSelect(nextType);
        inner.style.transition = 'none';
        inner.style.transform = `translateX(${w}px)`;
        requestAnimationFrame(() => {
          inner.style.transition = `transform ${SLIDE_DURATION_MS}ms ease-out`;
          inner.style.transform = 'translateX(0)';
          inner.ontransitionend = () => {
            inner.ontransitionend = null;
            slideBusy = false;
            clearTransform();
          };
        });
      };
    } else {
      inner.style.transition = `transform ${SLIDE_DURATION_MS}ms ease-out`;
      inner.style.transform = `translateX(${w}px)`;
      inner.ontransitionend = async () => {
        inner.ontransitionend = null;
        const prevType = wrapIdx(getSelectedType() - 1);
        await onSelect(prevType);
        inner.style.transition = 'none';
        inner.style.transform = `translateX(${-w}px)`;
        requestAnimationFrame(() => {
          inner.style.transition = `transform ${SLIDE_DURATION_MS}ms ease-out`;
          inner.style.transform = 'translateX(0)';
          inner.ontransitionend = () => {
            inner.ontransitionend = null;
            slideBusy = false;
            clearTransform();
          };
        });
      };
    }
  };
  const onPtrDown = (e: PointerEvent) => {
    startX = e.clientX;
    dragging = true;
    canvasWrap.setPointerCapture(e.pointerId);
  };
  const onPtrMove = (e: PointerEvent) => {
    if (!dragging) return;
    applyTx(e.clientX - startX);
  };
  const onPtrUp = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    try { canvasWrap.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    const dx = e.clientX - startX;
    if (dx < -SWIPE_THRESHOLD) slideTo('next');
    else if (dx > SWIPE_THRESHOLD) slideTo('prev');
    else {
      inner.style.transition = `transform ${SLIDE_DURATION_MS}ms ease-out`;
      inner.style.transform = 'translateX(0)';
      inner.ontransitionend = () => {
        inner.ontransitionend = null;
        clearTransform();
      };
    }
  };
  canvasWrap.addEventListener('pointerdown', onPtrDown);
  canvasWrap.addEventListener('pointermove', onPtrMove);
  canvasWrap.addEventListener('pointerup', onPtrUp);
  canvasWrap.addEventListener('pointercancel', onPtrUp);
  canvasWrap.addEventListener('wheel', (e) => {
    e.preventDefault();
    const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (d > 0) slideTo('next');
    else if (d < 0) slideTo('prev');
  }, { passive: false });

  return { wrap: canvasWrap, canvas };
}
